"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, X, RotateCcw, Check, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import Webcam from "react-webcam";

export const CameraCapture = ({ isOpen, onClose, onCapture }) => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [hasCamera, setHasCamera] = useState(true);
  const [cameraFacingMode, setCameraFacingMode] = useState("environment");
  const [isCameraReady, setIsCameraReady] = useState(false);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  // Check for camera availability
  useEffect(() => {
    const checkCameraAvailability = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setHasCamera(videoDevices.length > 0);
      } catch (err) {
        console.error("Error checking devices:", err);
        setHasCamera(false);
      }
    };

    if (isOpen) {
      checkCameraAvailability();
    }
  }, [isOpen]);

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `receipt-${Date.now()}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          setCapturedImage({
            url: imageSrc,
            file
          });
        });
    }
  }, []);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
  }, []);

  const handleClose = useCallback(() => {
    setCapturedImage(null);
    setError(null);
    onClose();
  }, [onClose]);


  // Confirm and upload
  const confirmCapture = useCallback(() => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage.file);
      handleClose();
    }
  }, [capturedImage, onCapture, handleClose]);
  


  const downloadImage = useCallback(() => {
    if (capturedImage) {
      const link = document.createElement('a');
      link.href = capturedImage.url;
      link.download = `receipt-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [capturedImage]);

  // Switch camera
  const switchCamera = useCallback(() => {
    setCameraFacingMode(prev => 
      prev === "user" ? "environment" : "user"
    );
  }, []);

  const videoConstraints = {
    facingMode: cameraFacingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };

  const onUserMediaError = (error) => {
    console.error("Camera error:", error);
    setError("Failed to access camera. Please check permissions and try again.");
    setHasCamera(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Capture Receipt</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-auto space-y-4">
          {!capturedImage ? (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                {hasCamera ? (
                  <>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      onUserMedia={() => setIsCameraReady(true)}
                      onUserMediaError={onUserMediaError}
                      className="w-full h-auto max-h-[60vh] object-contain"
                    />
                    <div className="absolute inset-0 border-2 border-dashed border-white opacity-50 m-8 pointer-events-none"></div>
                    <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                      Position receipt within the frame
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 flex flex-col items-center justify-center h-full">
                    <Camera className="h-16 w-16 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">No camera available</p>
                    <p className="text-sm text-gray-500">
                      Please use the file upload option instead
                    </p>
                  </div>
                )}
              </div>

              {hasCamera && (
                <div className="flex justify-center space-x-4 pt-4">
                  <Button onClick={switchCamera} variant="outline" className="flex items-center">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Switch Camera
                  </Button>
                  <Button
                    onClick={capturePhoto}
                    className="bg-green-500 hover:bg-green-600 text-white flex items-center"
                    disabled={!isCameraReady}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Capture Photo
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Preview</h3>
                <div className="relative bg-gray-100 rounded-lg overflow-hidden inline-block">
                  <img
                    src={capturedImage.url}
                    alt="Captured receipt"
                    className="max-w-full max-h-[60vh] object-contain"
                  />
                </div>
              </div>

              <div className="flex justify-center space-x-4 pt-4">
                <Button onClick={retakePhoto} variant="outline" className="flex items-center">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button onClick={downloadImage} variant="outline" className="flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={confirmCapture}
                  className="bg-green-500 hover:bg-green-600 text-white flex items-center"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Use This Photo
                </Button>
              </div>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};